/**
 * The single locale-formatting seam.
 *
 * The defect this closes
 * ─────────────────────
 * 103 production call sites format numbers, money and dates by naming the
 * locale inline — `n.toLocaleString('en-US', …)`, `new Intl.NumberFormat('en-US')`,
 * `d.toLocaleDateString('en-US', …)`. Two separate failures follow from that:
 *
 *   1. The locale is WRONG. A pinned `'en-US'` renders `1,234.5` to a German
 *      supervisor whose record says `1.234,5`. Removing the literal and passing
 *      `undefined` is not the fix either — that formats in whatever locale the
 *      operator's OS happens to be set to, which is a different value again for
 *      the same record.
 *
 *   2. The SAME value renders two ways. This tree has already been bitten by
 *      exactly this: two sibling fields in one file used different midpoint
 *      rounding, and `Tyndo.Core.Finance.Percentage` exists because a 0–1
 *      fraction was being rendered as a percent in one projection and
 *      multiplied by 100 again in its sibling. Scattering `Intl` construction
 *      across 103 sites makes locale formatting a second place for the same
 *      class of divergence: one site writes `maximumFractionDigits: 3`, its
 *      neighbour writes `2`, and the grid and the export disagree about what
 *      the record says.
 *
 * What this module fixes
 * ──────────────────────
 * Every formatter is constructed HERE, against `resolveActiveLocale()`, with
 * three policies pinned in one place:
 *
 *   • ROUNDING. Every numeric formatter is built with an explicit
 *     `roundingMode: 'halfExpand'`. `Intl` already defaults to half-expand, but
 *     leaving it implicit is what let the two-sibling-fields defect happen at
 *     the C# layer — the default was invisible, so nobody noticed when one site
 *     changed it. It is now stated, in one line, for the whole product.
 *
 *   • SCALE. A ratio is never passed as a bare number. `formatPercentOfFraction`
 *     takes 0–1 and `formatPercentOfPercent` takes 0–100; there is no
 *     `formatPercent`. This is the `Percentage` discipline expressed in the
 *     presentation layer, so the 100× mistake cannot be made by picking the
 *     wrong helper name by accident.
 *
 *   • PRECISION DEFAULTS. `DECIMALS` names the four precision classes the
 *     product actually uses (count / money / measure / factor) so a caller
 *     chooses a meaning rather than typing a digit count.
 *
 * Non-hook by design
 * ──────────────────
 * These are plain functions, not hooks, because most of the 103 call sites are
 * module-scope helpers (`const fmt = (n) => …`) or pure `lib/` logic that
 * cannot run a hook. They read the locale through the same
 * `resolveActiveLocale()` registration seam the React tree renders under, so an
 * imperative helper and a component render the identical string. Components
 * that must RE-RENDER on an in-session language change should use the
 * `use*Format` hooks in `useFormatters.ts`, which are built on this module's
 * cache.
 */

import { resolveActiveLocale } from './useActiveLocale.ts';
import { formatInPlantTz, resolvePlantTimeZone } from './usePlantTimeZone.ts';

/**
 * The one midpoint-rounding mode the product presents with.
 *
 * Stated rather than defaulted: an implicit rounding mode is precisely how two
 * sibling fields in one file came to round differently. Changing this changes
 * every rendered number in the product, which is the point — it is a reviewable
 * one-line decision instead of 103 independent ones.
 */
export const MIDPOINT_ROUNDING = 'halfExpand' as const;

/**
 * Named precision classes. Callers pick a MEANING, not a digit count, so the
 * same kind of value renders with the same precision in the grid, the detail
 * panel and the export.
 */
export const DECIMALS = {
  /** Whole things — units produced, alarm counts, row totals. */
  count: 0,
  /** Money presented to an operator. Ledger math is decimal on the server. */
  money: 2,
  /** A measured physical quantity — mass, length, tonnes CO₂e, cycle seconds. */
  measure: 3,
  /** A derived coefficient — emission factor, absorption rate, shadow price. */
  factor: 4,
} as const;

export type DecimalClass = keyof typeof DECIMALS;

// ── Formatter cache ────────────────────────────────────────────────────
// 103 call sites, many inside table-cell render functions, must not construct
// an `Intl` object per row. Keyed on locale + kind + the serialized options, so
// two callers asking for the same format share one instance.

const numberCache = new Map<string, Intl.NumberFormat>();
const dateCache = new Map<string, Intl.DateTimeFormat>();
const collatorCache = new Map<string, Intl.Collator>();

function cacheKey(locale: string, options: unknown): string {
  return `${locale} ${JSON.stringify(options ?? null)}`;
}

/**
 * A cached `Intl.NumberFormat` for `locale` with the product's rounding mode
 * pinned. Exported for the hook layer and for tests that assert the pinning;
 * view code should call the named `format*` helpers instead.
 */
export function numberFormatter(
  locale: string,
  options?: Intl.NumberFormatOptions,
): Intl.NumberFormat {
  const withPolicy: Intl.NumberFormatOptions = {
    roundingMode: MIDPOINT_ROUNDING,
    ...options,
  } as Intl.NumberFormatOptions;
  const key = cacheKey(locale, withPolicy);
  let fmt = numberCache.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat(locale, withPolicy);
    } catch {
      // An unsupported option (an old engine without `roundingMode`) or an
      // unresolvable locale must not throw through a render.
      fmt = new Intl.NumberFormat(locale, options);
    }
    numberCache.set(key, fmt);
  }
  return fmt;
}

/** A cached `Intl.DateTimeFormat` for `locale`. */
export function dateFormatter(
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = cacheKey(locale, options);
  let fmt = dateCache.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(locale, options);
    dateCache.set(key, fmt);
  }
  return fmt;
}

/**
 * Clear the memoized `Intl` instances. Called by the shell when the active
 * locale changes mid-session (the cache is keyed on locale so stale entries are
 * never *returned*, but a long session that switches languages should not hold
 * both sets), and by tests between locale fixtures.
 */
export function resetFormatterCache(): void {
  numberCache.clear();
  dateCache.clear();
  collatorCache.clear();
}

// ── The blank ──────────────────────────────────────────────────────────

/**
 * What a formatter renders for a value that is absent or not a number.
 *
 * An em-dash is a TYPOGRAPHIC placeholder, not prose: it needs no translation
 * and is read identically in every locale the product ships. Returning `''`
 * instead would make an empty cell indistinguishable from a cell whose value
 * failed to load, and returning `'N/A'` would be an untranslated English
 * string in a regulated record.
 */
export const BLANK = '—';

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') return Number(value);
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ── Numbers ────────────────────────────────────────────────────────────

export interface NumberFormatOptions {
  /** Explicit locale. Defaults to the resolved active locale. */
  locale?: string;
  /** Named precision class, or an explicit maximum fraction-digit count. */
  decimals?: DecimalClass | number;
  /** Minimum fraction digits (defaults to 0, i.e. trailing zeros are dropped). */
  minDecimals?: number;
  /** Rendered when the value is nullish or non-finite. Defaults to {@link BLANK}. */
  blank?: string;
  /** Group separators (`1,234`). Defaults to true. */
  grouping?: boolean;
}

function resolveDecimals(d: DecimalClass | number | undefined, fallback: number): number {
  if (d === undefined) return fallback;
  if (typeof d === 'number') return d;
  return DECIMALS[d];
}

/**
 * Format a number in the active locale. The replacement for every
 * `n.toLocaleString('en-US', …)` in the tree.
 */
export function formatNumber(value: unknown, options: NumberFormatOptions = {}): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.blank ?? BLANK;
  const max = resolveDecimals(options.decimals, DECIMALS.count);
  const min = Math.min(options.minDecimals ?? 0, max);
  return numberFormatter(options.locale ?? resolveActiveLocale(), {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: options.grouping ?? true,
  }).format(n);
}

/** Whole-number convenience — the `DECIMALS.count` class. */
export function formatCount(value: unknown, options: NumberFormatOptions = {}): string {
  return formatNumber(value, { decimals: 'count', ...options });
}

/**
 * Format money. `currency` is an ISO-4217 code; a missing or malformed code
 * degrades to a plain decimal rather than throwing through a render, because a
 * bad currency code on one row must not blank the whole grid.
 */
export function formatCurrency(
  value: unknown,
  currency: string | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.blank ?? BLANK;
  const locale = options.locale ?? resolveActiveLocale();
  const digits = resolveDecimals(options.decimals, DECIMALS.money);
  if (!currency || !/^[A-Za-z]{3}$/.test(currency)) {
    return formatNumber(n, { ...options, decimals: digits, minDecimals: digits });
  }
  return numberFormatter(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: options.grouping ?? true,
  }).format(n);
}

/**
 * Format a ratio held as a FRACTION (0–1) as a percent.
 *
 * There is deliberately no `formatPercent`. A bare ratio does not say which
 * scale it is on, and this tree has already shipped the 100× error that follows
 * — see `Tyndo.Core.Finance.Percentage`. The scale is in the function name so
 * the mistake is visible at the call site.
 */
export function formatPercentOfFraction(
  value: unknown,
  options: NumberFormatOptions = {},
): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.blank ?? BLANK;
  const max = resolveDecimals(options.decimals, 1);
  return numberFormatter(options.locale ?? resolveActiveLocale(), {
    style: 'percent',
    minimumFractionDigits: Math.min(options.minDecimals ?? 0, max),
    maximumFractionDigits: max,
  }).format(n);
}

/**
 * Format a ratio ALREADY expressed as a percent (0–100). Counterpart to
 * {@link formatPercentOfFraction}; see that doc for why both exist.
 */
export function formatPercentOfPercent(
  value: unknown,
  options: NumberFormatOptions = {},
): string {
  const n = toFiniteNumber(value);
  if (n === null) return options.blank ?? BLANK;
  const max = resolveDecimals(options.decimals, 1);
  return numberFormatter(options.locale ?? resolveActiveLocale(), {
    style: 'percent',
    minimumFractionDigits: Math.min(options.minDecimals ?? 0, max),
    maximumFractionDigits: max,
  }).format(n / 100);
}

/**
 * Format a physical quantity with its unit of measure.
 *
 * The UoM symbol is passed through, not translated: `kg`, `mm` and `tCO₂e` are
 * SI / GHG-Protocol symbols that are the same token in every locale, and
 * "translating" them would break the record. What IS localized is the number
 * and the separator — `Intl`'s `unit` style is not used because it only covers
 * the CLDR sanctioned unit list, and this product renders plant-defined UoMs.
 */
export function formatQuantity(
  value: unknown,
  uom: string | null | undefined,
  options: NumberFormatOptions = {},
): string {
  const text = formatNumber(value, { decimals: 'measure', ...options });
  if (text === (options.blank ?? BLANK)) return text;
  const unit = (uom ?? '').trim();
  // U+00A0 NO-BREAK SPACE: the value and its unit must never wrap apart.
  return unit ? `${text} ${unit}` : text;
}

// ── Dates and times ────────────────────────────────────────────────────

/**
 * `Intl.DateTimeFormatOptions` with `timeZone` REPLACED rather than extended.
 *
 * `Intl` types `timeZone` as `string | undefined`, where `undefined` means
 * "the host's zone". This module needs a third state: `undefined` must mean
 * "the resolved PLANT zone" (the manufacturing default), and an explicit
 * `null` is how a caller asks for browser-local. Widening the inherited
 * property is not legal — an interface may not narrow-to-wider on extension —
 * so the base property is omitted and redeclared.
 */
export interface DateFormatOptions extends Omit<Intl.DateTimeFormatOptions, 'timeZone'> {
  /** Explicit locale. Defaults to the resolved active locale. */
  locale?: string;
  /**
   * Explicit IANA timezone. Defaults to the resolved PLANT timezone — a
   * manufacturing timestamp is read in the plant's wall clock, not the
   * reviewer's browser. Pass `'UTC'` to pin, or `null` for browser-local.
   */
  timeZone?: string | null;
  /** Rendered for a nullish/invalid instant. Defaults to {@link BLANK}. */
  blank?: string;
}

function formatInstant(
  value: Date | number | string | null | undefined,
  preset: Intl.DateTimeFormatOptions,
  options: DateFormatOptions,
): string {
  const { locale, timeZone, blank, ...overrides } = options;
  if (value == null || value === '') return blank ?? BLANK;
  const resolvedLocale = locale ?? resolveActiveLocale();
  const opts = { ...preset, ...overrides };
  const zone = timeZone === null ? undefined : (timeZone ?? resolvePlantTimeZone());
  const text = zone
    ? formatInPlantTz(value, zone, opts, resolvedLocale)
    : (() => {
        const d = value instanceof Date ? value : new Date(value);
        return Number.isNaN(d.getTime()) ? '' : dateFormatter(resolvedLocale, opts).format(d);
      })();
  return text === '' ? (blank ?? BLANK) : text;
}

/** Date only, medium form (`5 Aug 2026` / `05.08.2026`), in plant time. */
export function formatDate(
  value: Date | number | string | null | undefined,
  options: DateFormatOptions = {},
): string {
  return formatInstant(value, { dateStyle: 'medium' }, options);
}

/** Date + time, medium/short (`5 Aug 2026, 14:32`), in plant time. */
export function formatDateTime(
  value: Date | number | string | null | undefined,
  options: DateFormatOptions = {},
): string {
  return formatInstant(value, { dateStyle: 'medium', timeStyle: 'short' }, options);
}

/** Time only, short (`14:32` / `2:32 PM`), in plant time. */
export function formatTime(
  value: Date | number | string | null | undefined,
  options: DateFormatOptions = {},
): string {
  return formatInstant(value, { timeStyle: 'short' }, options);
}

// ── Search and sorting ─────────────────────────────────────────────────

/**
 * A cached locale collator.
 *
 * `a.localeCompare(b)` with no locale sorts in the browser's locale, so the
 * same table sorts differently for two operators looking at the same data. It
 * also gets `ä` wrong for a German list (`ä` sorts with `a`, not after `z`) and
 * gets Swedish exactly the opposite way — which is why the locale has to be the
 * resolved one and not a guess.
 */
export function collator(locale?: string, options?: Intl.CollatorOptions): Intl.Collator {
  const loc = locale ?? resolveActiveLocale();
  const key = cacheKey(loc, options ?? null);
  let c = collatorCache.get(key);
  if (!c) {
    c = new Intl.Collator(loc, { numeric: true, sensitivity: 'variant', ...options });
    collatorCache.set(key, c);
  }
  return c;
}

/**
 * Locale-correct string comparison for table sorting. `numeric: true` so
 * `LOT-2` sorts before `LOT-10`.
 */
export function compareStrings(a: string, b: string, locale?: string): number {
  return collator(locale).compare(a, b);
}

/**
 * Fold a string for locale-insensitive SEARCH matching: case-folded and
 * diacritic-stripped, so typing `charge` finds `Chargenfreigabe` and typing
 * `prufung` finds `Prüfung`.
 *
 * This is the search counterpart to {@link compareStrings}: sorting must honour
 * locale collation, but a substring filter must IGNORE the accents the operator
 * did not type. Using `toLowerCase()` alone gets Turkish dotless-i wrong, so the
 * fold is `toLocaleLowerCase(locale)` followed by canonical decomposition with
 * the combining marks removed.
 */
export function foldForSearch(value: string, locale?: string): string {
  const loc = locale ?? resolveActiveLocale();
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase(loc);
}

/** True when `haystack` contains `needle` under {@link foldForSearch}. */
export function matchesSearch(haystack: string, needle: string, locale?: string): boolean {
  const n = needle.trim();
  if (n === '') return true;
  return foldForSearch(haystack, locale).includes(foldForSearch(n, locale));
}
